/**
 * NEXUMA — Auto-guarded Routes
 *
 * Derives a path → permission requirements map from SIDEBAR_CONFIG so that
 * sidebar visibility and route protection share a single source of truth.
 * Adding a new module to the sidebar automatically protects its route too.
 *
 * Usage:
 *   <GuardedRoute path="/salarieslist" component={SalariesList} />
 *
 * GuardedRoute looks up `/salarieslist` in ROUTE_PERMISSIONS and wraps the
 * route with RouteGuard using the matching `requiresAny` / `requiresAll`.
 * If no match is found, the route renders unguarded (treated as public),
 * unless an explicit `requires` / `requiresAny` / `requiresAll` is passed.
 *
 * Sub-routes (e.g. `/holidays/addholiday`) that aren't in the sidebar fall
 * back to their parent's permissions via longest-prefix match.
 */

import React from 'react';
import { Route } from 'react-router-dom';
import { SIDEBAR_CONFIG } from '../../config/permissions/sidebarConfig';
import RouteGuard from './RouteGuard';

// ── Flatten sidebar tree into a path → requirements lookup ──────────────────
function flattenSidebar(items, acc = []) {
  for (const item of items || []) {
    if (item.to) {
      acc.push({
        path:        item.to,
        exact:       !!item.exact,
        requiresAny: item.requiresAny,
        requiresAll: item.requiresAll,
      });
      // extraPaths lets a single sidebar item cover multiple routes
      // (e.g. fte-plan also serves /oret); reuse the same permissions.
      for (const extra of item.extraPaths || []) {
        acc.push({
          path:        extra,
          exact:       false,
          requiresAny: item.requiresAny,
          requiresAll: item.requiresAll,
        });
      }
    }
    if (item.children) flattenSidebar(item.children, acc);
  }
  return acc;
}

export const ROUTE_PERMISSIONS = flattenSidebar(SIDEBAR_CONFIG);

// Longest-prefix match — a sub-route like /holidays/addholiday inherits the
// permissions of its parent /holidays. Exact matches always win.
export function getRouteRequirements(path) {
  if (!path) return null;
  const exact = ROUTE_PERMISSIONS.find((r) => r.path === path);
  if (exact) return exact;
  const prefixMatch = ROUTE_PERMISSIONS
    .filter((r) => path.startsWith(r.path + '/'))
    .sort((a, b) => b.path.length - a.path.length)[0];
  return prefixMatch || null;
}

// ── GuardedRoute — drop-in replacement for <Route> ──────────────────────────
export default function GuardedRoute({
  path,
  exact,
  strict,
  component,
  children,
  // Explicit overrides — if any are passed, they take precedence over the
  // sidebar-derived requirements (useful for routes not in the sidebar).
  requires,
  requiresAny,
  requiresAll,
  requiresRole,
  requiresAnyRole,
  redirectTo,
  silent,
}) {
  const explicit = requires || requiresAny || requiresAll || requiresRole || requiresAnyRole;
  const auto = explicit ? null : getRouteRequirements(path);

  // No requirements found and none provided → render as plain Route. This is
  // intentional: it keeps the migration safe (existing routes don't break)
  // while still benefitting from the sidebar's source of truth.
  if (!explicit && !auto) {
    return (
      <Route path={path} exact={exact} strict={strict}>
        {component ? React.createElement(component) : children}
      </Route>
    );
  }

  return (
    <RouteGuard
      path={path}
      exact={exact ?? auto?.exact}
      strict={strict}
      component={component}
      requires={requires}
      requiresAny={requiresAny || auto?.requiresAny}
      requiresAll={requiresAll || auto?.requiresAll}
      requiresRole={requiresRole}
      requiresAnyRole={requiresAnyRole}
      redirectTo={redirectTo}
      silent={silent}
    >
      {children}
    </RouteGuard>
  );
}
